import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { SubscriptionPlan } from '../../types/auth';
interface SubscriptionTierBadgeProps {
  subscriptionPlan: SubscriptionPlan;
  isOnTrial?: boolean;
  trialDaysLeft?: number;
  onUpgrade?: () => void;
  showUpgradeButton?: boolean;
  compact?: boolean;
}

export const SubscriptionTierBadge: React.FC<SubscriptionTierBadgeProps> = ({
  subscriptionPlan,
  isOnTrial = false,
  trialDaysLeft,
  onUpgrade,
  showUpgradeButton = true,
  compact = false,
}) => {
  // Show upgrade for free, plus, trial, pay-per-scan (not for Pro)
  const effectiveShowUpgradeButton =
    showUpgradeButton &&
    subscriptionPlan !== SubscriptionPlan.PRO &&
    !!onUpgrade;

  const getSubscriptionInfo = () => {
    switch (subscriptionPlan) {
      case SubscriptionPlan.FREE:
        return {
          label: 'Basic',
          icon: 'account-outline' as const,
          colors: ['#6B7280', '#9CA3AF'],
          textColor: '#FFFFFF',
        };
      case SubscriptionPlan.PLUS:
        return {
          label: 'Plus',
          icon: 'star-outline' as const,
          colors: ['#9475e1', '#A78BFA'],
          textColor: '#FFFFFF',
        };
      case SubscriptionPlan.FREEMIUM_TRIAL:
        return {
          label: 'Trial',
          icon: 'star-circle-outline' as const,
          colors: ['#F59E0B', '#FBBF24'],
          textColor: '#FFFFFF',
        };
      case SubscriptionPlan.PRO:
        return {
          label: 'Pro',
          icon: 'crown' as const,
          colors: ['#8B5CF6', '#A78BFA'],
          textColor: '#FFFFFF',
        };
      case SubscriptionPlan.PAY_PER_SCAN:
        return {
          label: 'Pay-per-scan',
          icon: 'qrcode-scan' as const,
          colors: ['#059669', '#10B981'],
          textColor: '#FFFFFF',
        };
      default:
        return {
          label: 'Basic',
          icon: 'account-outline' as const,
          colors: ['#6B7280', '#9CA3AF'],
          textColor: '#FFFFFF',
        };
    }
  };

  const info = getSubscriptionInfo();
  const showUpgrade = effectiveShowUpgradeButton;

  if (compact) {
    return (
      <View style={styles.compactContainer}>
        <LinearGradient colors={info.colors} style={styles.compactBadge}>
          <MaterialCommunityIcons
            name={info.icon}
            size={12}
            color={info.textColor}
            style={{ marginRight: 4 }}
          />
          <Text style={[styles.compactText, { color: info.textColor }]}>
            {info.label}
          </Text>
        </LinearGradient>

        {showUpgrade && onUpgrade && (
          <TouchableOpacity
            onPress={onUpgrade}
            style={styles.compactUpgradeButton}
          >
            <Text style={styles.compactUpgradeText}>Upgrade</Text>
          </TouchableOpacity>
        )}
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <View style={styles.leftSection}>
          <LinearGradient colors={info.colors} style={styles.iconContainer}>
            <MaterialCommunityIcons
              name={info.icon}
              size={24}
              color={info.textColor}
            />
          </LinearGradient>

          <View style={styles.textContainer}>
            <Text style={styles.label}>{info.label}</Text>
            {isOnTrial && trialDaysLeft !== undefined && (
              <Text style={styles.trialText}>
                {trialDaysLeft > 0
                  ? `${trialDaysLeft} days remaining`
                  : 'Trial expired'}
              </Text>
            )}
            {subscriptionPlan === SubscriptionPlan.FREE && !isOnTrial && (
              <Text style={styles.limitedText}>
                Limited features — upgrade for more
              </Text>
            )}
          </View>
        </View>

        {showUpgrade && onUpgrade && (
          <TouchableOpacity onPress={onUpgrade} style={styles.upgradeButton}>
            <Text style={styles.upgradeButtonText}>Upgrade</Text>
          </TouchableOpacity>
        )}
      </View>

      {/* Trial expiration warning */}
      {isOnTrial && trialDaysLeft !== undefined && trialDaysLeft <= 2 && (
        <View style={styles.warningContainer}>
          <View style={styles.warningHeader}>
            <MaterialCommunityIcons
              name="alert-circle-outline"
              size={16}
              color="#EA580C"
              style={{ marginRight: 8 }}
            />
            <Text style={styles.warningTitle}>
              {trialDaysLeft > 0
                ? `Trial ends in ${trialDaysLeft} day${trialDaysLeft === 1 ? '' : 's'}`
                : 'Trial has ended'}
            </Text>
          </View>
          <Text style={styles.warningSubtitle}>
            Upgrade to Pro to keep accessing premium features
          </Text>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    padding: 16,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  leftSection: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  iconContainer: {
    width: 48,
    height: 48,
    borderRadius: 24,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  textContainer: {
    flex: 1,
  },
  label: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
  },
  trialText: {
    fontSize: 14,
    color: '#EA580C',
    marginTop: 2,
  },
  limitedText: {
    fontSize: 14,
    color: '#6B7280',
    marginTop: 2,
  },
  upgradeButton: {
    backgroundColor: '#8B5CF6',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
  },
  upgradeButtonText: {
    color: '#FFFFFF',
    fontWeight: '500',
  },
  warningContainer: {
    marginTop: 12,
    backgroundColor: '#FEF3C7',
    borderWidth: 1,
    borderColor: '#FDE68A',
    borderRadius: 8,
    padding: 12,
  },
  warningHeader: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  warningTitle: {
    color: '#92400E',
    fontSize: 14,
    fontWeight: '500',
  },
  warningSubtitle: {
    color: '#B45309',
    fontSize: 14,
    marginTop: 4,
  },
  compactContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  compactBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    flexDirection: 'row',
    alignItems: 'center',
  },
  compactText: {
    fontSize: 12,
    fontWeight: '500',
  },
  compactUpgradeButton: {
    marginLeft: 8,
    backgroundColor: '#F3E8FF',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  compactUpgradeText: {
    color: '#7C3AED',
    fontSize: 12,
    fontWeight: '500',
  },
});
